import React from "react";
import { useTranslation, Trans } from "react-i18next";
import { GrownupsHurdleBody } from "../../components/GrownupsHurdleBody";
import {
  AnalyticsContext,
  ViewGrownupsHurdleEvent,
  GrownupsHurdleContinueButtonTapEvent,
  GrownupsHurdleFailedEvent,
  GrownupsHurdlePassedEvent,
} from "../../common/context/AnalyticsContext";
import {
  AppFlowStepContainer,
  AppFlowTitle,
  AppFlowStepContentContainer,
  AppFlowContinueButton,
  AppFlowModal,
} from "../../common/CommonStyles";

const enum State {
  Initial = 1,
  TooYoungError = 2,
  InvalidError = 3,
  TooManyAttemptsError = 4,
  Success = 5,
}

const LOCKOUT_END_DATE_KEY = "LOCKOUT_END_DATE_KEY";
const shortLockoutTimeSeconds = 10;
const longLockoutTimeSeconds = 3600 * 24; // 24 hours

const getDurationToTargetDate = (targetDate: Date): string | null => {
  /**
   * @returns string in hh:mm:ss format for the amount of time until targetDate. Or,
   * returns null if the target date has passed
   */
  const duration = targetDate.getTime() - Date.now();
  if (duration <= 0) {
    return null;
  }
  const pad = (x: number) => x.toString().padStart(2, "0");
  const hours = Math.floor(duration / (1000 * 60 * 60));
  const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((duration % (1000 * 60)) / 1000);
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

export const GrownupsHurdle: React.FC<{ next: () => void }> = ({ next }) => {
  const { t } = useTranslation();
  const { logEvent } = React.useContext(AnalyticsContext);
  const [canClickContinue, setCanClickContinue] = React.useState(false);
  const [month, setMonth] = React.useState("");
  const [day, setDay] = React.useState("");
  const [year, setYear] = React.useState("");
  const [state, setState] = React.useState(State.Initial);
  const [, setTick] = React.useState(0);
  const [lockoutEndDate, setLockoutEndDate] = React.useState<Date | null>(null);
  const [showErrorModal, setShowErrorModal] = React.useState(false);

  const shouldLogViewGrownupsHurdle = React.useRef(true);
  const hasProcessedFailure = React.useRef(false);
  const failureCount = React.useRef(0);

  if (shouldLogViewGrownupsHurdle.current) {
    logEvent(new ViewGrownupsHurdleEvent());
    shouldLogViewGrownupsHurdle.current = false;
  }

  React.useEffect(() => {
    const lockoutDateString = localStorage.getItem(LOCKOUT_END_DATE_KEY);
    if (lockoutDateString) {
      // We only store if the user failed too many times. Setting the state like this
      // makes sure the UI is rendered correctly
      const targetDate = new Date(lockoutDateString);
      const duration = targetDate.getTime() - Date.now();
      if (duration <= 0) {
        setLockoutEndDate(null);
        localStorage.removeItem(LOCKOUT_END_DATE_KEY);
      } else {
        setLockoutEndDate(targetDate);
        setState(State.TooManyAttemptsError);
      }
    }
  }, [setLockoutEndDate, setState]);

  React.useEffect(() => {
    if (lockoutEndDate !== null) {
      const timer = setInterval(() => {
        setTick((x) => x + 1);
        if (lockoutEndDate.getTime() < Date.now()) {
          setLockoutEndDate(null);
          setTick(0);
          setState(State.Initial);
          localStorage.removeItem(LOCKOUT_END_DATE_KEY);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [lockoutEndDate, setTick]);

  const updateLockoutDateAndStartTimer = React.useCallback(
    (lockoutTimeSeconds: number, persist: boolean = false) => {
      const date = new Date(Date.now() + lockoutTimeSeconds * 1000);
      setLockoutEndDate(date);

      if (persist) {
        localStorage.setItem(LOCKOUT_END_DATE_KEY, date.toISOString());
      }
    },
    [setLockoutEndDate]
  );

  const processFailure = React.useCallback(() => {
    if (hasProcessedFailure.current) {
      return;
    }
    const tooManyAttempts = state === State.TooManyAttemptsError;
    const lockoutTimeSeconds = tooManyAttempts
      ? longLockoutTimeSeconds
      : shortLockoutTimeSeconds;

    // Only show error modal if not first time wrong
    if (failureCount.current % 3 !== 1) {
      setShowErrorModal(true);
    } else {
      hasProcessedFailure.current = true;
      updateLockoutDateAndStartTimer(lockoutTimeSeconds, tooManyAttempts);
    }
  }, [state, setShowErrorModal, updateLockoutDateAndStartTimer]);

  const verify = React.useCallback(() => {
    hasProcessedFailure.current = false;

    const birthDate = new Date(+year, +month, +day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    let m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    let newState;
    if (+year < 1910 || +day < 1 || +day > 31) {
      newState = State.InvalidError;
    } else if (age < 18) {
      newState = State.TooYoungError;
    } else {
      newState = State.Success;
    }

    if (newState === State.Success) {
      logEvent(new GrownupsHurdlePassedEvent(failureCount.current + 1));
    } else {
      failureCount.current = failureCount.current + 1;

      const isLockedOut = failureCount.current % 3 === 0;
      if (isLockedOut) {
        const date = new Date(Date.now() + longLockoutTimeSeconds * 1000);
        localStorage.setItem(LOCKOUT_END_DATE_KEY, date.toISOString());
        newState = State.TooManyAttemptsError;
      }

      // NOTE: didInteract is only applicable on mobile, as the user has to interact with
      // the UI to get to this LOC
      logEvent(new GrownupsHurdleFailedEvent(isLockedOut, true));
    }

    setState(newState);
  }, [month, day, year, setState, logEvent]);

  React.useEffect(() => {
    const existsEmptyField = month === "" || day === "" || year === "";
    setCanClickContinue(!existsEmptyField);
  }, [month, day, year]);

  React.useEffect(() => {
    switch (state) {
      case State.Initial:
        // Intentionally left empty
        break;
      case State.Success:
        next();
        break;
      default:
        processFailure();
    }
  }, [state, processFailure, next]);

  return (
    <AppFlowStepContainer>
      <AppFlowModal
        title={t("grownups_hurdle_error_title")}
        body={
          state === State.TooManyAttemptsError
            ? t("grownups_hurdle_too_many_failed_attempts_error_message")
            : t("grownups_hurdle_default_error_message")
        }
        closeButtonText={t("generic_ok")}
        shouldShowModal={showErrorModal}
        closeModal={() => {
          hasProcessedFailure.current = true;

          const tooManyAttempts = state === State.TooManyAttemptsError;
          const lockoutTimeSeconds = tooManyAttempts
            ? longLockoutTimeSeconds
            : shortLockoutTimeSeconds;
          if (lockoutEndDate === null) {
            updateLockoutDateAndStartTimer(lockoutTimeSeconds, tooManyAttempts);
          }
          setShowErrorModal(false);
        }}
      />
      <AppFlowTitle
        title={t("grownups_hurdle_title_text_reason")}
        subtitle={t("grownups_hurdle_subtitle_text_reason")}
      />
      <AppFlowStepContentContainer>
        <div className="pt-3 d-flex flex-grow-1 flex-column">
          <GrownupsHurdleBody
            day={day}
            setDay={setDay}
            month={month}
            setMonth={setMonth}
            year={year}
            setYear={setYear}
          />
          <div className="d-flex align-items-end justify-content-center flex-grow-1 gap-2">
            <div className="d-flex align-items-center justify-content-center flex-column text-danger">
              {lockoutEndDate && (
                <div>
                  {state === State.TooManyAttemptsError ? (
                    <Trans
                      t={t}
                      i18nKey="grownups_hurdle_too_many_attempts_error"
                      values={{
                        duration: getDurationToTargetDate(lockoutEndDate),
                      }}
                    />
                  ) : state === State.TooYoungError ? (
                    <Trans
                      t={t}
                      i18nKey="grownups_hurdle_too_young_error"
                      values={{
                        duration: getDurationToTargetDate(lockoutEndDate),
                      }}
                    />
                  ) : (
                    <Trans
                      t={t}
                      i18nKey="grownups_hurdle_invalid_error"
                      values={{
                        duration: getDurationToTargetDate(lockoutEndDate),
                      }}
                    />
                  )}
                </div>
              )}
              <AppFlowContinueButton
                text={t("app_flow_continue_button")}
                enabled={canClickContinue && lockoutEndDate === null}
                onClick={() => {
                  logEvent(new GrownupsHurdleContinueButtonTapEvent());
                  verify();
                }}
              />
            </div>
          </div>
        </div>
      </AppFlowStepContentContainer>
    </AppFlowStepContainer>
  );
};
